// StageExamPage — the proctored written test for a pipeline stage.
//
// The rules, as the school was told them (walkthrough, 30 Oct 2026) and as
// the server applies them again on submit (examProctoring.service):
//   - camera AND microphone are required, asked for once before the exam;
//   - who is sitting it: a code to the candidate's mobile, a photo of them
//     and a photo of their photo ID (examIdentity.service) — the questions
//     are not sent until that is done;
//   - leaving the exam (another tab or application): a warning the first and
//     second time; the third time the exam ends and the candidate is
//     disqualified;
//   - five serious violations (copy/paste, a second face, no face, the
//     microphone going off) end it the same way;
//   - photos every few minutes and at each violation — to S3 through the
//     API; the browser keeps none of them;
//   - countdown timer — submits on expiry.
//
// Candidate NEVER sees score, answer key, or evaluation outcome.

import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../../services/api";
import { useFaceDetector, useNoiseDetector } from "../common/ProctorGuard";
import {
  AlertTriangle, Camera, CheckCircle, Clock, Maximize2,
  Shield, ChevronRight, Loader2, EyeOff, Smartphone, IdCard, XCircle
} from "lucide-react";
import useSchoolName from "./useSchoolName";

// Overwritten by the server's own numbers (exam data `proctoring`).
const DEFAULT_RULES = { maxLeaves: 3, maxSerious: 5, photoEveryMinutes: 2 };
const LEAVE_TYPES   = new Set(['tab_switch', 'focus_lost']);
const SERIOUS_TYPES = new Set(['copy_paste', 'multi_face', 'no_face', 'mic_off']);
const SAME_LEAVE_MS = 2000; // a tab switch fires "hidden" and "blur": one leaving

// Leavings, counted as the server counts them.
export function countLeaves(list) {
  const times = list.filter((v) => LEAVE_TYPES.has(v.type)).map((v) => Date.parse(v.timestamp) || 0).sort((a, b) => a - b);
  let n = 0; let last = -Infinity;
  for (const t of times) { if (t - last > SAME_LEAVE_MS) n += 1; last = t; }
  return n;
}
const countSerious = (list) => list.filter((v) => SERIOUS_TYPES.has(v.type)).length;

// A JPEG of the camera, small enough for the API (well under 70 KB).
function grab(video, width = 320, quality = 0.6) {
  if (!video || video.readyState < 2 || !video.videoWidth) return null;
  try {
    const height = Math.round(width * (video.videoHeight / video.videoWidth));
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    canvas.getContext('2d').drawImage(video, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch { return null; }
}

export default function StageExamPage({ jobId, candidateId, stageId, jobTitle, stageLabel, onComplete }) {
  const { schoolName, examGuideUrl } = useSchoolName();
  // The token from the candidate's own link (…?t=…).
  const stageToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('t') || '' : '';
  const [phase, setPhase]       = useState("loading"); // loading|setup|exam|submitting|done|error
  const [examData, setExamData] = useState(null);
  const [answers, setAnswers]   = useState({});   // { [qId]: { selectedAnswer, selectedAnswers, writtenAnswer } }
  const [timeLeft, setTimeLeft] = useState(0);
  const [violations, setViolations] = useState([]);
  const [violationMsg, setViolationMsg] = useState("");
  const [violationModal, setViolationModal] = useState(null); // { type, description } — prominent overlay
  const [cameraGranted, setCameraGranted] = useState(false);
  const [cameraError, setCameraError]   = useState("");
  const [error, setError]       = useState("");
  const [autoSubmitReason, setAutoSubmitReason] = useState(null);
  const [faceWarning, setFaceWarning]   = useState("");  // current face warning message
  const [doneInfo, setDoneInfo] = useState(null);        // { disqualified, message }
  // The identity check: code, then the two photos.
  const [code, setCode]           = useState("");
  const [codeState, setCodeState] = useState({ sent: false, busy: false, msg: "", to: "" });
  const [photos, setPhotos]       = useState({ start: null, id_card: null }); // previews of what was sent
  const [photoBusy, setPhotoBusy] = useState("");
  const [photoMsg, setPhotoMsg]   = useState("");
  const [micStream, setMicStream] = useState(null);

  const videoRef      = useRef(null);
  const examVideoRef  = useRef(null);
  const streamRef     = useRef(null);
  const timerRef      = useRef(null);
  const photoTimerRef = useRef(null);
  const startTimeRef  = useRef(null);
  const submittingRef = useRef(false);  // guard against double-submit
  const violationsRef = useRef([]);     // the list as submitted, never a stale copy

  const rules = { ...DEFAULT_RULES, ...(examData?.proctoring || {}) };
  const identity = examData?.identity || { needed: false };

  const loadExam = useCallback(async () => {
    try {
      // The candidate's signed link token (?t=…) opens this paper for them.
      const r = await api.get(`/public/jobs/${jobId}/stage/${stageId}/exam`, { params: { resumeId: candidateId, t: stageToken } });
      if (r.data?.success) {
        setExamData(r.data.data);
        setTimeLeft(r.data.data.timeLimit * 60);
        setPhase((p) => (p === "loading" ? "setup" : p));
      } else {
        setError(r.data?.message || "Could not load exam.");
        setPhase("error");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Could not load exam.");
      setPhase("error");
    }
  }, [jobId, stageId, candidateId, stageToken]);

  useEffect(() => { loadExam(); }, [loadExam]);

  // Send one photo to the school (S3 through the API). Never throws.
  const sendPhoto = useCallback(async (kind, image, violationType = '') => {
    if (!image) return false;
    try {
      await api.post(`/public/jobs/${jobId}/stage/${stageId}/exam-photo`, { resumeId: candidateId, t: stageToken, kind, image, violationType });
      return true;
    } catch { return false; }
  }, [jobId, stageId, candidateId, stageToken]);

  // Log a violation, photograph the moment, and apply the limits.
  const logViolation = useCallback((type, description) => {
    const entry = { type, timestamp: new Date().toISOString(), description };
    const next = [...violationsRef.current, entry];
    violationsRef.current = next;
    setViolations(next);
    sendPhoto('violation', grab(examVideoRef.current || videoRef.current), type);
    const leaves = countLeaves(next);
    const serious = countSerious(next);
    let shown = description;
    if (LEAVE_TYPES.has(type)) {
      const left = rules.maxLeaves - leaves;
      shown = left > 0
        ? `You left the exam window (warning ${leaves} of ${rules.maxLeaves - 1}). If you leave ${left === 1 ? 'once more' : `${left} more times`}, the exam ends and you are disqualified.`
        : 'You left the exam window a third time. The exam is ending.';
    }
    setViolationMsg(`⚠ ${shown}`);
    setViolationModal({ type, description: shown });
    if (leaves >= rules.maxLeaves) setAutoSubmitReason("left_exam");
    else if (serious >= rules.maxSerious) setAutoSubmitReason("violations");
  }, [sendPhoto, rules.maxLeaves, rules.maxSerious]);

  // ── Noise/speech detection during exam (the microphone already open) ────
  useNoiseDetector({
    active: phase === "exam",
    stream: micStream,
    onNoise: useCallback((noiseType) => {
      const type = noiseType || 'background_noise';
      const msg  = type === 'background_speech'
        ? "Background speech or voices detected. Please ensure a quiet environment."
        : "Excessive background noise detected. Please ensure a quiet environment.";
      logViolation(type, msg);
    }, [logViolation]),
  });

  // The microphone going off mid-exam (unplugged, muted by the system,
  // permission withdrawn) is a serious violation.
  useEffect(() => {
    if (phase !== "exam" || !micStream) return undefined;
    const tracks = micStream.getAudioTracks();
    const off = () => logViolation("mic_off", "Microphone turned off or disconnected");
    tracks.forEach((t) => { t.addEventListener("ended", off); t.addEventListener("mute", off); });
    return () => tracks.forEach((t) => { t.removeEventListener("ended", off); t.removeEventListener("mute", off); });
  }, [phase, micStream, logViolation]);

  // ── Face detection (active during exam, uses examVideoRef) ──────────────
  useFaceDetector({
    active: phase === "exam",
    videoRef: examVideoRef,
    onNoFace: useCallback(() => {
      setFaceWarning("You are not visible on camera. Please stay in front of your webcam.");
      logViolation("no_face", "Candidate not visible on camera");
    }, [logViolation]),
    onMultiFace: useCallback((count) => {
      setFaceWarning(`${count} people detected on camera. Only the candidate should be visible.`);
      logViolation("multi_face", `Multiple people detected on camera (${count})`);
    }, [logViolation]),
  });

  // Proctoring event listeners (active only during exam)
  useEffect(() => {
    if (phase !== "exam") return;
    const onHide     = () => { if (document.hidden) logViolation("tab_switch", "Tab switched away from exam"); };
    const onBlur     = () => logViolation("focus_lost", "Exam window lost focus");
    const onCopy     = () => logViolation("copy_paste", "Copy detected during exam");
    const onPaste    = () => logViolation("copy_paste", "Paste detected during exam");
    const onFsChg    = () => { if (!document.fullscreenElement) logViolation("fullscreen_exit", "Exam exited fullscreen mode"); };
    const onCtxMenu  = (e) => { e.preventDefault(); logViolation("right_click", "Right-click attempted during exam"); };
    const onKeyDown  = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['t','n','w','r'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        logViolation("keyboard_shortcut", `Keyboard shortcut blocked (${e.ctrlKey ? 'Ctrl' : 'Cmd'}+${e.key.toUpperCase()})`);
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    document.addEventListener("copy",        onCopy);
    document.addEventListener("paste",       onPaste);
    document.addEventListener("fullscreenchange", onFsChg);
    document.addEventListener("contextmenu", onCtxMenu);
    document.addEventListener("keydown",     onKeyDown);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("copy",        onCopy);
      document.removeEventListener("paste",       onPaste);
      document.removeEventListener("fullscreenchange", onFsChg);
      document.removeEventListener("contextmenu", onCtxMenu);
      document.removeEventListener("keydown",     onKeyDown);
    };
  }, [phase, logViolation]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "exam") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setAutoSubmitReason("timeout"); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // A photo every few minutes while the exam runs.
  useEffect(() => {
    if (phase !== "exam") return undefined;
    photoTimerRef.current = setInterval(() => {
      sendPhoto('interval', grab(examVideoRef.current));
    }, Math.max(1, rules.photoEveryMinutes) * 60_000);
    return () => clearInterval(photoTimerRef.current);
  }, [phase, rules.photoEveryMinutes, sendPhoto]);

  // Auto-submit trigger
  useEffect(() => {
    if (autoSubmitReason) handleSubmit(autoSubmitReason);
  }, [autoSubmitReason]); // eslint-disable-line

  // Attach stream to exam mini-view when exam starts
  useEffect(() => {
    if (phase === "exam" && examVideoRef.current && streamRef.current) {
      examVideoRef.current.srcObject = streamRef.current;
    }
  }, [phase]);

  /* Camera AND microphone, together, once, before anything else (30 Oct
     2026). Both are required: the exam does not start without either. The
     same stream is used for the whole exam — the noise check listens to it —
     so the browser never asks again mid-exam (which used to steal focus and
     be logged as a violation). */
  const requestCamera = async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (!stream.getAudioTracks().length) {
        stream.getTracks().forEach((t) => t.stop());
        throw Object.assign(new Error('no microphone'), { name: 'NoMicrophone' });
      }
      streamRef.current = stream;
      setMicStream(stream);
      setCameraGranted(true);
    } catch (e) {
      setCameraError(e?.name === 'NotFoundError' || e?.name === 'NoMicrophone'
        ? "We could not find a camera and a microphone. Both are needed for this exam — please connect them and try again."
        : "Camera and microphone access are both required to begin the exam. Please allow them in your browser settings and try again.");
    }
  };

  // Attach stream to setup preview once video element is in the DOM
  useEffect(() => {
    if (cameraGranted && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraGranted, phase]);

  // ── Identity check ───────────────────────────────────────────────────────
  const sendCode = async () => {
    setCodeState((s) => ({ ...s, busy: true, msg: "" }));
    try {
      const r = await api.post(`/public/jobs/${jobId}/stage/${stageId}/exam-code/send`, { resumeId: candidateId, t: stageToken });
      const d = r.data?.data || {};
      if (d.skip) { setCodeState({ sent: false, busy: false, msg: d.message || "", to: "" }); await loadExam(); return; }
      setCodeState({ sent: true, busy: false, msg: `We have sent a 6-digit code on WhatsApp to ${d.to}.`, to: d.to });
    } catch (e) {
      setCodeState((s) => ({ ...s, busy: false, msg: e.response?.data?.message || "The code could not be sent. Please try again." }));
    }
  };

  const verifyCode = async () => {
    setCodeState((s) => ({ ...s, busy: true }));
    try {
      const r = await api.post(`/public/jobs/${jobId}/stage/${stageId}/exam-code/verify`, { resumeId: candidateId, t: stageToken, code });
      if (r.data?.success) { setCodeState((s) => ({ ...s, busy: false, msg: "" })); await loadExam(); }
      else setCodeState((s) => ({ ...s, busy: false, msg: r.data?.message || "That code is not right." }));
    } catch (e) {
      setCodeState((s) => ({ ...s, busy: false, msg: e.response?.data?.message || "The code could not be checked. Please try again." }));
    }
  };

  const takePhoto = async (kind) => {
    setPhotoMsg("");
    // The ID needs its print to be readable: a larger, sharper picture.
    const image = kind === 'id_card' ? grab(videoRef.current, 560, 0.7) : grab(videoRef.current, 400, 0.65);
    if (!image) { setPhotoMsg("The camera is not ready yet. Wait a moment and try again."); return; }
    setPhotoBusy(kind);
    const ok = await sendPhoto(kind, image);
    setPhotoBusy("");
    if (!ok) { setPhotoMsg("The photo could not be sent. Please try again."); return; }
    const next = { ...photos, [kind]: image };
    setPhotos(next);
    if (next.start && next.id_card) await loadExam();
  };

  const startExam = async () => {
    try { await document.documentElement.requestFullscreen(); } catch { /* user may deny */ }
    startTimeRef.current = Date.now();
    setPhase("exam");
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const handleSubmit = useCallback(async (reason = "manual") => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPhase("submitting");
    clearInterval(timerRef.current);
    clearInterval(photoTimerRef.current);
    stopCamera();
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});

    const timeTaken = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;

    // Build answers payload from state
    const answersPayload = Object.entries(answers).map(([questionId, ans]) => ({
      questionId,
      selectedAnswer:  ans.selectedAnswer  || null,
      selectedAnswers: ans.selectedAnswers || [],
      writtenAnswer:   ans.writtenAnswer   || "",
    }));

    try {
      const r = await api.post(`/public/jobs/${jobId}/stage/${stageId}/exam-submit`, {
        resumeId:   candidateId,
        t:          stageToken,
        answers:    answersPayload,
        violations: violationsRef.current,
        endedBy:    reason,
        timeTaken,
      });
      if (r.data?.success) {
        setDoneInfo({ disqualified: Boolean(r.data.data?.disqualified), message: r.data.data?.message || "" });
        setPhase("done");
        if (!r.data.data?.disqualified) setTimeout(() => onComplete?.({ overallScore: null }), 3500);
      } else {
        setError(r.data?.message || "Submission failed.");
        setPhase("error");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Submission failed.");
      setPhase("error");
    }
  }, [answers, jobId, stageId, candidateId, onComplete, stageToken]);

  const setAnswer = (qId, patch) => setAnswers(a => ({ ...a, [qId]: { ...(a[qId] || {}), ...patch } }));

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const answeredCount = Object.keys(answers).length;
  const totalQ        = examData?.questions?.length || 0;
  const timerCritical = timeLeft <= 120;

  // ── Loading ──────────────────────────────────────────────────────────────
  if (phase === "loading") return (
    <div className="flex items-center justify-center min-h-screen bg-[#1E2A33]">
      <div className="text-center text-white">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-[#0B7A82]" />
        <p className="text-lg">Loading exam…</p>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────
  if (phase === "error") return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full mx-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Exam unavailable</h2>
        <p className="text-gray-600 text-sm">{error}</p>
      </div>
    </div>
  );

  // ── Setup ────────────────────────────────────────────────────────────────
  if (phase === "setup") return (
    <div className="min-h-screen bg-[#faf7f4] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
        <div className="text-center mb-6">
          <img src={`${process.env.PUBLIC_URL}/assets/dps-crest.png`} alt={schoolName}
            className="h-14 mx-auto mb-3" />
          <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase">{schoolName}</p>
          <h1 className="text-2xl font-bold text-[#1E2A33] mt-1">{stageLabel}</h1>
          <p className="text-gray-500 text-sm mt-1">{jobTitle}</p>
        </div>

        {/* No surprises: this is a proctored exam and the candidate hears it
            here, before anything is recorded — what is watched, what counts
            as a violation, and what happens at the limit. A warning that
            first appears mid-exam reads as an accusation; the same words up
            front read as fairness. */}
        {examGuideUrl && (
          <a href={examGuideUrl} target="_blank" rel="noopener noreferrer"
            className="block bg-teal-50 border border-teal-200 rounded-xl p-3 mb-4 text-sm text-teal-800 font-medium">
            ▶ Watch first: how the online exam works (opens in a new tab)
          </a>
        )}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm">
          <p className="font-semibold text-amber-800 mb-2">This is a proctored exam</p>
          <p className="text-amber-800 mb-2">
            While the exam runs, your webcam checks that you are present and alone, and the
            page notes if you switch tabs, leave fullscreen, or copy and paste. Each of these
            is recorded as a violation with the time it happened:
          </p>
          <ul className="list-disc list-inside space-y-1 text-amber-800">
            <li>Moving out of camera view, or another person appearing</li>
            <li>Switching to another tab or application</li>
            <li>Leaving fullscreen</li>
            <li>Copying or pasting</li>
            <li>Turning the microphone off, or voices in the room</li>
          </ul>
          <p className="text-amber-800 mt-2 font-semibold">
            Leaving the exam window: you get a warning the first and second time. The third
            time, the exam ends and you are disqualified. {rules.maxSerious} other serious
            violations end it the same way.
          </p>
          <p className="text-amber-800 mt-2">
            Your camera and microphone stay on for the whole exam, and photos are taken every
            few minutes. The record is seen only by the hiring team, alongside your answers.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-sm">
          <p className="font-semibold text-blue-800 mb-2">Exam rules</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
            {/* First rule is always dynamic — question count + time limit */}
            <li>{totalQ} questions — {examData?.timeLimit} minutes</li>
            {/* Remaining rules: use HR-configured rules if available, else hardcoded defaults */}
            {(examData?.examRules?.length
              ? examData.examRules
              : [
                  'Exam runs in fullscreen — do not exit',
                  'Exam auto-submits when time runs out',
                  'Scores and results are shared only with the hiring team',
                ]
            ).map((rule, i) => <li key={i}>{rule}</li>)}
          </ul>
          {/* Custom instructions from HR */}
          {examData?.candidateInstructions && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="font-semibold text-blue-800 mb-1">Additional instructions</p>
              <p className="text-blue-700 whitespace-pre-wrap text-sm">{examData.candidateInstructions}</p>
            </div>
          )}
        </div>

        {/* Step 1 — camera and microphone (both required) */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Camera className="w-4 h-4" /> 1. Camera and microphone
          </p>
          {!cameraGranted ? (
            <div>
              <button onClick={requestCamera}
                className="w-full py-2.5 bg-[#1E2A33] text-white rounded-xl text-sm font-medium hover:bg-[#1a1718] transition-colors">
                Allow camera and microphone
              </button>
              {cameraError && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {cameraError}
                </p>
              )}
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden bg-black h-48">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              <span className="absolute top-2 left-2 flex items-center gap-1 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                ● Camera and microphone on
              </span>
            </div>
          )}
        </div>

        {/* Steps 2 and 3 — who is sitting the exam. The questions are not
            sent until both are done (examIdentity.service). */}
        {identity.needed && cameraGranted && (
          <div className="mb-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Smartphone className="w-4 h-4" /> 2. Confirm your mobile
                {identity.codeDone && <CheckCircle className="w-4 h-4 text-green-600" />}
              </p>
              {!identity.codeDone && (
                <div className="space-y-2">
                  {!codeState.sent ? (
                    <button onClick={sendCode} disabled={codeState.busy}
                      className="w-full py-2.5 bg-[#1E2A33] text-white rounded-xl text-sm font-medium disabled:opacity-50">
                      {codeState.busy ? 'Sending…' : 'Send me a code on WhatsApp'}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" aria-label="Code"
                        className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm tracking-widest" />
                      <button onClick={verifyCode} disabled={codeState.busy || code.length < 4}
                        className="px-4 py-2 bg-[#0B7A82] text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                        {codeState.busy ? 'Checking…' : 'Confirm'}
                      </button>
                    </div>
                  )}
                  {codeState.msg && <p className="text-xs text-gray-600">{codeState.msg}</p>}
                  {codeState.sent && <button onClick={sendCode} disabled={codeState.busy} className="text-xs text-[#0B7A82] underline">Send a new code</button>}
                </div>
              )}
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <IdCard className="w-4 h-4" /> 3. Your photo and your photo ID
                {identity.photosDone && <CheckCircle className="w-4 h-4 text-green-600" />}
              </p>
              {!identity.photosDone && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    First a photo of you, looking at the camera. Then hold your photo ID (Aadhaar card,
                    driving licence, passport or PAN card) close to the camera so your photo on it can
                    be seen, and take the second photo.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[['start', 'Take my photo'], ['id_card', 'Take photo of my ID']].map(([kind, label]) => (
                      <div key={kind} className="space-y-1">
                        {photos[kind]
                          ? <img src={photos[kind]} alt={kind === 'start' ? 'Your photo' : 'Your ID'} className="w-full h-20 object-cover rounded-lg border" />
                          : <div className="w-full h-20 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-[11px] text-gray-400">{kind === 'start' ? 'Your photo' : 'Your ID'}</div>}
                        <button onClick={() => takePhoto(kind)} disabled={Boolean(photoBusy)}
                          className="w-full py-2 bg-[#1E2A33] text-white rounded-lg text-xs font-medium disabled:opacity-50">
                          {photoBusy === kind ? 'Sending…' : photos[kind] ? 'Take again' : label}
                        </button>
                      </div>
                    ))}
                  </div>
                  {photoMsg && <p className="text-xs text-amber-700">{photoMsg}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        <button onClick={startExam} disabled={!cameraGranted || identity.needed || !totalQ}
          className="w-full py-3 bg-[#0B7A82] text-white rounded-xl text-sm font-semibold hover:bg-[#075358] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
          <Maximize2 className="w-4 h-4" />
          I understand — begin the proctored exam
          <ChevronRight className="w-4 h-4" />
        </button>
        {!cameraGranted && <p className="text-xs text-center text-gray-400 mt-2">Allow camera and microphone access to begin</p>}
        {cameraGranted && identity.needed && <p className="text-xs text-center text-gray-400 mt-2">Finish steps 2 and 3 to begin</p>}
      </div>
    </div>
  );

  // ── Submitting ───────────────────────────────────────────────────────────
  if (phase === "submitting") return (
    <div className="flex items-center justify-center min-h-screen bg-[#1E2A33]">
      <div className="text-center text-white">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-[#0B7A82]" />
        <p className="text-lg font-semibold">Submitting your exam…</p>
        <p className="text-sm text-white/60 mt-1">Please wait</p>
      </div>
    </div>
  );

  // ── Done ─────────────────────────────────────────────────────────────────
  if (phase === "done") return (
    <div className="flex items-center justify-center min-h-screen bg-[#faf7f4]">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-8 text-center">
        <img src={`${process.env.PUBLIC_URL}/assets/dps-crest.png`} alt={schoolName}
          className="h-12 mx-auto mb-3" />
        {doneInfo?.disqualified ? (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Exam ended — proctoring failed</h2>
            <p className="text-gray-600 text-sm mb-4">{doneInfo.message}</p>
          </>
        ) : (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Exam Submitted</h2>
            <p className="text-gray-600 text-sm mb-4">
              Thank you for completing the assessment. Your responses have been recorded and will be reviewed by the hiring team.
            </p>
          </>
        )}
        <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500">
          <EyeOff className="w-5 h-5 mx-auto mb-1 text-gray-400" />
          Results and scores are visible only to recruiters and are not shared with candidates.
        </div>
      </div>
    </div>
  );

  // ── Exam ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col select-none">
      {/* Proctoring violation overlay — shown for ALL violation types */}
      {(faceWarning || violationModal) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center border-4 border-orange-400">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-xl font-extrabold mb-2 text-orange-700">Proctoring Violation Detected</h2>
            <p className="text-gray-700 text-sm mb-3 leading-relaxed">
              {faceWarning || violationModal?.description}
            </p>
            <div className="bg-orange-50 rounded-lg px-4 py-2 mb-5 text-xs text-orange-700 font-medium">
              Left the exam: <strong>{countLeaves(violations)} of {rules.maxLeaves}</strong> &nbsp;·&nbsp; Serious violations: <strong>{countSerious(violations)} of {rules.maxSerious}</strong>
            </div>
            <button
              onClick={() => { setFaceWarning(""); setViolationModal(null); }}
              className="w-full py-3 rounded-xl font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors"
            >
              I Understand — Return to Exam
            </button>
          </div>
        </div>
      )}
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-[#1E2A33] text-white px-6 py-3 flex items-center justify-between shadow-lg">
        <div>
          <p className="font-bold text-sm">{stageLabel}</p>
          <p className="text-xs text-white/60">{jobTitle}</p>
        </div>
        <div className="flex items-center gap-4">
          {violations.length > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold text-red-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {violations.length} violation{violations.length > 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-white/70">{answeredCount}/{totalQ} answered</span>
          <span className={`flex items-center gap-1.5 font-mono font-bold text-sm px-3 py-1 rounded-full ${
            timerCritical ? "bg-red-500 animate-pulse" : "bg-white/10"
          }`}>
            <Clock className="w-3.5 h-3.5" />
            {formatTime(timeLeft)}
          </span>
          {cameraGranted && streamRef.current && (
            <video ref={examVideoRef} autoPlay muted playsInline
              className="w-16 h-12 rounded-lg object-cover border-2 border-white/20" />
          )}
        </div>
      </div>

      {/* Violation banner */}
      {violationMsg && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2 flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {violationMsg}
          <button onClick={() => setViolationMsg("")} className="ml-auto text-xs text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Questions */}
      <div className="flex-1 max-w-3xl mx-auto w-full p-6 space-y-6">
        {(examData?.questions || []).map((q, idx) => {
          const ans     = answers[q._id] || {};
          const qType   = q.questionType || "mcq";
          const answered = qType === "multi_select"
            ? (ans.selectedAnswers?.length > 0)
            : qType === "short_answer" || qType === "long_answer" || qType === "scenario" || qType === "case_study"
            ? !!ans.writtenAnswer?.trim()
            : !!ans.selectedAnswer;

          return (
            <div key={q._id} className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-colors ${answered ? "border-[#1E2A33]" : "border-gray-200"}`}>
              {/* Question header */}
              <div className="flex items-start gap-3 mb-4">
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${answered ? "bg-[#1E2A33] text-white" : "bg-gray-100 text-gray-500"}`}>
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <span className="text-[10px] font-semibold uppercase text-gray-400 mr-2">{q.difficulty}</span>
                  <span className="text-[10px] font-semibold uppercase text-orange-400">{q.points}pts</span>
                  <p className="text-gray-800 font-medium leading-relaxed mt-1 whitespace-pre-wrap">{q.question}</p>
                </div>
              </div>

              {/* Answer input by type */}
              <div className="ml-10">
                {/* MCQ / Single Select / True-False */}
                {["mcq","single_select","true_false"].includes(qType) && (
                  <div className="space-y-2">
                    {(q.options || []).map((opt, oi) => (
                      <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                        ans.selectedAnswer === opt ? "border-[#1E2A33] bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}>
                        <input type="radio" name={`q-${q._id}`} value={opt}
                          checked={ans.selectedAnswer === opt}
                          onChange={() => setAnswer(q._id, { selectedAnswer: opt })}
                          className="accent-[#1E2A33]" />
                        <span className="text-sm text-gray-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Multi-Select */}
                {qType === "multi_select" && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 mb-1">Select all that apply</p>
                    {(q.options || []).map((opt, oi) => {
                      const selected = (ans.selectedAnswers || []).includes(opt);
                      return (
                        <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                          selected ? "border-[#1E2A33] bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}>
                          <input type="checkbox" checked={selected}
                            onChange={() => {
                              const cur = ans.selectedAnswers || [];
                              setAnswer(q._id, { selectedAnswers: selected ? cur.filter(x => x !== opt) : [...cur, opt] });
                            }}
                            className="accent-[#1E2A33]" />
                          <span className="text-sm text-gray-700">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Short Answer */}
                {qType === "short_answer" && (
                  <textarea rows={3} placeholder="Write your answer here…"
                    value={ans.writtenAnswer || ""}
                    onChange={e => setAnswer(q._id, { writtenAnswer: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2A33]/30 resize-none" />
                )}

                {/* Long Answer / Scenario / Case Study */}
                {["long_answer","scenario","case_study"].includes(qType) && (
                  <textarea rows={6} placeholder="Write your detailed answer here…"
                    value={ans.writtenAnswer || ""}
                    onChange={e => setAnswer(q._id, { writtenAnswer: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2A33]/30 resize-none" />
                )}
              </div>
            </div>
          );
        })}

        {/* Submit */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm">
          <p className="text-sm text-gray-500 mb-3">
            {answeredCount < totalQ
              ? `${totalQ - answeredCount} question(s) unanswered — you can still submit`
              : "All questions answered — ready to submit!"}
          </p>
          <button onClick={() => handleSubmit("manual")}
            className="px-8 py-3 bg-[#0B7A82] text-white rounded-xl font-semibold hover:bg-[#075358] transition-colors">
            Submit Exam
          </button>
        </div>
      </div>
    </div>
  );
}
